import { useMedplum } from '@medplum/react-hooks';
import { Alert, Center, Container, Stack, Text } from '@mantine/core';
import { notify } from '@/lib/notify';
import { IconAlertCircle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loading } from '../../components/Loading';
import { formatSignInError } from '../../lib/authLoginErrors';
import { getUserFacingErrorMessage } from '@/lib/userFacingError';

/**
 * Auth callback page that processes the authorization code from Medplum
 *
 * This page handles the final step of the OAuth flow:
 * 1. Auth0 redirects to Medplum with auth code
 * 2. Medplum exchanges code and redirects here with a new code
 * 3. This page calls medplum.processCode() to complete authentication
 */
export function AuthCallback(): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    if (errorParam) {
      // error_description comes from the identity provider's redirect; keep it out of production UI.
      setError(getUserFacingErrorMessage(new Error(errorDescription || errorParam), 'Authentication failed'));
      return;
    }

    if (!code) {
      setError('No authorization code received');
      return;
    }

    // Process the authorization code
    medplum
      .processCode(code)
      .then(() => {
        notify({
          title: 'Welcome',
          message: 'Successfully signed in',
          type: 'success',
        });
        navigate('/dashboard');
      })
      .catch((err: Error) => {
        console.error('Auth callback error:', err);
        setError(formatSignInError(err, 'Failed to complete authentication'));
      });
  }, [medplum, navigate, searchParams]);

  if (error) {
    return (
      <Container size="xl" py="xl">
        <Center h="100vh" bg="gray.0">
          <Stack w={400} gap="md">
            <Alert
              icon={<IconAlertCircle size={20} />}
              title="Authentication Error"
              color="red"
            >
              {error}
            </Alert>
            <Text
              size="sm"
              c="blue"
              ta="center"
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/signin')}
            >
              Return to Sign In
            </Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Center h="100vh" bg="gray.0">
        <Stack align="center" gap="md">
          <Loading fullScreen={false} />
          <Text c="dimmed">Completing sign in...</Text>
        </Stack>
      </Center>
    </Container>
  );
}
